import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalLogin } from './api';

/**
 * Login del portal del paciente. Estética propia (no comparte AppShell ni
 * colores del staff) — paleta calmada, mensajes en lenguaje no técnico.
 */
export function PortalLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [documento, setDocumento] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await portalLogin({ documento: documento.trim(), password: password.trim() });
      navigate('/portal');
    } catch (err) {
      setError(
        err instanceof Error
          ? 'Documento o contraseña incorrectos. Verifica la información que te entregamos.'
          : 'No fue posible iniciar sesión.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-teal-50 via-white to-sky-50 p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/logo-endomedical.png"
            alt="Endomedical"
            className="h-12 w-auto object-contain mx-auto mb-3"
          />
          <h1 className="text-2xl font-extrabold text-slate-900">
            Portal del paciente
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Endomedical IPS — consulta tus resultados clínicos
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Ingresa con tu <strong>número de documento (cédula)</strong> como usuario y
            contraseña.
          </p>
        </div>

        <form
          onSubmit={(e) => void submit(e)}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-7 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="documento" className="text-xs font-semibold text-slate-700">
              Número de documento
            </label>
            <input
              id="documento"
              type="text"
              autoComplete="username"
              required
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              placeholder="Tu cédula o documento de identidad"
              className="h-11 px-3 rounded-lg border border-slate-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-xs font-semibold text-slate-700">
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu número de documento (cédula)"
              className="h-11 px-3 rounded-lg border border-slate-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>

          {error !== null && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-11 rounded-lg bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50"
          >
            {submitting ? 'Ingresando…' : 'Ingresar a mis resultados'}
          </button>
        </form>

        <div className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} Endomedical IPS
        </div>
      </div>
    </div>
  );
}
